import { Col, Text } from 'components';
import { theme } from 'lib';
import { useTranslation } from 'react-i18next';
import { ParticipationSummary } from 'screens/Admin/components';
import { fmtDecimal, fmtDuration, fmtNumber, fmtRate } from '../format';

// Mirrors backend constants/goalRush.ts GoalRushResetReason.
const END_REASONS = ['target_reached', 'target_not_reached', 'admin_override', 'disabled'];

/**
 * Ranked bar list in the ModuleBreakdownTable / EffectUsageSummary style. `rows` is
 * [{ key, label, value, extra? }]; bars scale to the largest value.
 */
const BarList = ({ title, subtitle, rows, color, emptyText }) => {
  const max = Math.max(1, ...rows.map((r) => r.value || 0));

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <div className="px-5 py-3.5 border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <p className="text-[13px] font-semibold text-[#0F172A]">{title}</p>
        {subtitle && <p className="text-[11px] text-[#94A3B8]">{subtitle}</p>}
      </div>

      <div className="py-2 max-h-[320px] overflow-y-auto">
        {!rows.length && <p className="px-5 py-2.5 text-[13px] text-[#94A3B8]">{emptyText}</p>}
        {rows.map((row) => (
          <div key={row.key} className="flex items-center gap-3 px-5 py-2.5">
            <span className="shrink-0 text-[13px] font-medium text-[#334155] w-[150px] truncate">
              {row.label}
            </span>
            <span className="flex-1 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${Math.max(4, ((row.value || 0) / max) * 100)}%`,
                  background: color
                }}
              />
            </span>
            <span className="shrink-0 text-[13px] font-semibold text-[#0F172A] tabular-nums w-16 text-right">
              {fmtNumber(row.value)}
            </span>
            {row.extra != null && (
              <span className="shrink-0 text-[12px] text-[#64748B] tabular-nums w-20 text-right">
                {row.extra}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/** A tile row, with an optional BarList (`list`) under it once the figures are in. */
const Group = ({ title, items, list, loading }) => (
  <Col noFlex wid="100%" marg="12px 0 0">
    <Text b2 med col={theme.GREY_60}>
      {title}
    </Text>
    <ParticipationSummary loading={loading} items={items} />
    {list && !loading && (
      <div className="w-full mt-3">
        <BarList {...list} />
      </div>
    )}
  </Col>
);

/**
 * GoalRush analytics (brief §7) from `/admin/insights/goalrush`: volume, rates, milestones,
 * session lifecycle, engagement per fan, event throughput, reset reasons and kicks per day.
 *
 * `hideHeader` drops the title when a container (the analytics drawer) carries its own;
 * `stacked` puts the two bar lists in one column for narrow containers.
 */
const GoalRushStats = ({ data, loading, hideHeader = false, stacked = false }) => {
  const { t } = useTranslation();
  const k = (key, opts) => t(`admin.insights.goalrushStats.${key}`, opts);

  const volume = data?.volume;
  const rates = data?.rates;
  const lifecycle = data?.lifecycle;
  const milestones = data?.milestones;
  const participants = data?.participants;
  const events = data?.events;

  const endReasons = lifecycle?.endReasons || {};
  const reasonRows = [...END_REASONS, ...Object.keys(endReasons)]
    .filter((reason, i, all) => all.indexOf(reason) === i && endReasons[reason])
    .map((reason) => ({
      key: reason,
      label: t(`admin.insights.goalrushStats.endReason.${reason}`, {
        defaultValue: reason
      }),
      value: endReasons[reason]
    }));

  // Newest day first, so the period's latest activity is at the top of the list.
  const dayRows = [...(data?.daily || [])].reverse().map((d) => ({
    key: d.day,
    label: new Date(`${d.day}T00:00:00`).toLocaleDateString(),
    value: d.kicks,
    extra: k('goalsCount', { value: fmtNumber(d.goals) })
  }));

  const groups = [
    {
      title: k('groups.volume'),
      items: [
        { label: k('sessions'), value: fmtNumber(volume?.sessions), hint: k('sessionsHint') },
        { label: k('kicks'), value: fmtNumber(volume?.kicks) },
        { label: k('goals'), value: fmtNumber(volume?.goals) },
        { label: k('kicksPerSession'), value: fmtDecimal(volume?.kicksPerSession) },
        { label: k('goalsPerSession'), value: fmtDecimal(volume?.goalsPerSession) }
        // Hidden — moved to Engagement / dropped from the panel. Uncomment to switch back.
        // { label: k('fans'), value: fmtNumber(participants?.fans), hint: k('fansHint') },
        // { label: k('kickers'), value: fmtNumber(participants?.kickers) }
      ],
      list: data && {
        title: k('daily'),
        subtitle: k('dailyHint'),
        rows: dayRows,
        color: '#10B981',
        emptyText: k('empty')
      }
    },
    {
      title: k('groups.rates'),
      items: [
        { label: k('goalRate'), value: fmtRate(rates?.goalRate), hint: k('goalRateHint') },
        { label: k('blockRate'), value: fmtRate(rates?.blockRate), hint: k('blockRateHint') },
        // Hidden from the panel. Uncomment to switch back.
        // {
        //   label: k('defenderHitRate'),
        //   value: fmtRate(rates?.defenderHitRate),
        //   hint: k('defenderHitRateHint')
        // },
        {
          label: k('collisionRate'),
          value: fmtRate(rates?.collisionRate),
          hint: k('collisionRateHint')
        }
        // Hidden from the panel. Uncomment to switch back.
        // { label: k('nearMissRate'), value: fmtRate(rates?.nearMissRate) }
      ]
    },
    {
      title: k('groups.milestones'),
      items: ['kicks', 'goals'].flatMap((kind) => [
        {
          label: k(`milestone.${kind}.completion`),
          value: fmtRate(milestones?.[kind]?.completionRate),
          hint: k('milestone.reachedOf', {
            reached: fmtNumber(milestones?.[kind]?.reached),
            ended: fmtNumber(lifecycle?.endedSessions)
          })
        },
        {
          label: k(`milestone.${kind}.medianTime`),
          value: fmtDuration(milestones?.[kind]?.medianTimeToSec, t),
          hint: k('milestone.avg', { value: fmtDuration(milestones?.[kind]?.avgTimeToSec, t) })
        }
      ])
    },
    {
      title: k('groups.sessions'),
      items: [
        { label: k('endedSessions'), value: fmtNumber(lifecycle?.endedSessions) },
        {
          label: k('autoResets'),
          value: fmtNumber(lifecycle?.autoResets),
          hint: k('autoResetsHint')
        },
        {
          label: k('avgSessionDuration'),
          value: fmtDuration(lifecycle?.avgSessionDurationSec, t),
          hint: k('avgSessionDurationHint')
        }
        // Hidden from the panel. Uncomment to switch back.
        // {
        //   label: k('pausedByOverride'),
        //   value: fmtNumber(lifecycle?.pausedByOverride),
        //   hint: k('pausedByOverrideHint')
        // }
      ],
      list: data && {
        title: k('resetReasons'),
        subtitle: k('resetReasonsHint'),
        rows: reasonRows,
        color: '#6366F1',
        emptyText: k('empty')
      }
    },
    {
      title: k('groups.engagement'),
      items: [
        { label: k('fans'), value: fmtNumber(participants?.fans), hint: k('fansHint') },
        { label: k('avgKicksPerFan'), value: fmtDecimal(participants?.avgKicksPerFan) },
        { label: k('avgGoalsPerFan'), value: fmtDecimal(participants?.avgGoalsPerFan) }
        // Hidden from the panel. Uncomment to switch back.
        // { label: k('avgSessionsPerFan'), value: fmtDecimal(participants?.avgSessionsPerFan) }
      ]
    },
    {
      title: k('groups.events'),
      items: [
        { label: k('participationEvents'), value: fmtNumber(events?.participationEvents) },
        { label: k('goalEvents'), value: fmtNumber(events?.goalEvents) },
        {
          label: k('avgPerSec'),
          value: fmtDecimal(events?.avgPerSec, 2),
          hint: k('avgPerSecHint')
        },
        {
          label: k('peakPerSec'),
          value: fmtDecimal(events?.peakPerSec, 2),
          hint: k('peakPerSecHint')
        }
      ]
    }
  ];

  return (
    <Col noFlex wid="100%" marg={hideHeader ? '0' : '24px 0 0'}>
      {!hideHeader && (
        <>
          <Text h4>{k('title')}</Text>
          <Text c2 col={theme.GREY_40} marg="2px 0 0">
            {k('subtitle')}
          </Text>
        </>
      )}

      {groups.map((group) => (
        <Group
          key={group.title}
          title={group.title}
          items={group.items}
          list={group.list}
          loading={loading}
        />
      ))}

      {/* The two lists now sit under their groups (Volume, Sessions). Uncomment to switch
          back to the side-by-side pair at the bottom.
      {!loading && data && (
        <div className={`grid grid-cols-1 ${stacked ? '' : 'lg:grid-cols-2'} gap-5 w-full mt-4`}>
          <BarList
            title={k('resetReasons')}
            subtitle={k('resetReasonsHint')}
            rows={reasonRows}
            color="#6366F1"
            emptyText={k('empty')}
          />
          <BarList
            title={k('daily')}
            subtitle={k('dailyHint')}
            rows={dayRows}
            color="#10B981"
            emptyText={k('empty')}
          />
        </div>
      )}
      */}
    </Col>
  );
};

export default GoalRushStats;
